import { useState } from "react";
import { toast } from "sonner";
import { Trash, Plus, CheckCircle, XCircle } from "@phosphor-icons/react";
import { act, api, useFetch } from "@/lib/api";
import { Panel, Badge } from "@/components/common";
import { FixGuide } from "@/components/FixGuide";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const Tick = ({ ok, label, id }) => (
  <span className={`inline-flex items-center gap-1 ${ok ? "text-emerald-400" : "text-red-400"}`} data-testid={id}>{ok ? <CheckCircle weight="fill" /> : <XCircle weight="fill" />}{label}</span>
);

const Health = () => {
  const { data } = useFetch("/senders/health");
  if (!data) return null;
  return (
    <div className="mt-4 space-y-2 text-xs" data-testid="senders-health">
      <div className="text-zinc-300" data-testid="senders-capacity">
        {data.mailboxes} mailbox{data.mailboxes === 1 ? "" : "es"} · today up to <b className="text-white">{data.today_capacity}</b> cold emails · at full warm-up {data.full_capacity}/day.
        {" "}5,000 a day needs about {data.mailboxes_for_5000} mailboxes at {data.per_mailbox_max} each.
      </div>
      {data.domains.map((d) => (
        <div key={d.domain} className="flex flex-wrap gap-3 items-center" data-testid={`domain-health-${d.domain}`}>
          <span className="text-white w-40 truncate">{d.domain}</span>
          <Tick ok={d.mx} label="MX" id={`dns-mx-${d.domain}`} /><Tick ok={d.spf} label="SPF" id={`dns-spf-${d.domain}`} />
          <Tick ok={d.dkim} label="DKIM" id={`dns-dkim-${d.domain}`} /><Tick ok={d.dmarc} label="DMARC" id={`dns-dmarc-${d.domain}`} />
        </div>
      ))}
    </div>
  );
};

export const SendersPanel = ({ isAdmin }) => {
  const { data, reload } = useFetch("/senders");
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [guide, setGuide] = useState(false);
  const add = async () => {
    setBusy(true);
    const r = await act(() => api.post("/senders", { emails }));
    setBusy(false);
    if (r) { setRes(r); setEmails(""); reload(); }
  };
  const remove = async (e) => {
    if (!window.confirm(`Stop sending from ${e}? Its contacts move to the other mailboxes.`)) return;
    if (await act(() => api.delete(`/senders/${encodeURIComponent(e)}`), `${e} removed`)) reload();
  };
  if (!data) return null;
  return (
    <Panel title="Cold-email sending mailboxes" data-testid="senders-panel">
      <p className="text-xs text-zinc-500 mb-3">MaSa sends cold emails from these mailboxes (and Azure, when connected) in parallel, weekdays 7am-6pm Sydney. Each mailbox warms up by itself from 20 to 50 a day and pauses on bounces or blocks; follow-ups come from the sender of Email 1, and every reply goes to the main mailbox.</p>
      <ul className="space-y-1.5 text-sm max-h-56 overflow-auto">
        <li className="flex items-center gap-2" data-testid="sender-primary">{data.primary || "Not connected"} <Badge v="main" tone="blue" /></li>
        {data.extra.map((x) => (
          <li key={x.email} className="flex items-center gap-2" data-testid={`sender-${x.email}`}>
            {x.email} <Badge v="verified" tone="green" />
            {isAdmin && <Button size="sm" variant="ghost" className="ml-auto h-7" data-testid={`sender-remove-${x.email}`} onClick={() => remove(x.email)}><Trash /></Button>}
          </li>
        ))}
      </ul>
      <Health key={data.extra.length} />
      {isAdmin && data.primary && (
        <div className="mt-4 space-y-2">
          <Textarea data-testid="sender-add-input" rows={3} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="Paste one or many mailboxes, e.g. pawan@getspecia.com, alex@tryspecia.com" className="bg-black text-sm" />
          <Button data-testid="sender-add-btn" disabled={busy || !emails.trim()} onClick={add} className="gap-1.5"><Plus /> {busy ? "Checking each mailbox…" : "Add mailboxes"}</Button>
          {res && (
            <div className="text-xs space-y-1" data-testid="sender-add-result">
              {res.added.length > 0 && <div className="text-emerald-400">Added: {res.added.join(", ")}</div>}
              {res.failed.map((f) => <div key={f.email} className="text-red-400">{f.email}: {f.error}</div>)}
            </div>
          )}
        </div>
      )}
      <Button variant="link" size="sm" className="px-0 mt-2 text-[#F97316]" data-testid="senders-guide-toggle" onClick={() => setGuide(!guide)}>{guide ? "Hide" : "Show"} the setup guide for 5,000 a day</Button>
      {guide && <FixGuide id="cold-domains" guide={data.guide} />}
    </Panel>
  );
};

const ColdLane = ({ data, isAdmin, reload }) => {
  const [cc, setCc] = useState(data.cold_cc || "");
  const save = async (enabled) => { if (await act(() => api.post("/acs/cold", { enabled, cc }), "Saved")) reload(); };
  return (
    <div className="mt-3 p-3 rounded border border-border/60 space-y-2 text-xs" data-testid="acs-cold">
      <div className="flex items-center gap-2">
        <Switch data-testid="acs-cold-toggle" checked={data.cold} disabled={!isAdmin || data.managed} onCheckedChange={save} />
        <span className="text-white text-sm">{data.cold ? "Azure sends cold email alongside the Outlook mailboxes" : "Only the Outlook mailboxes send cold email"}</span>
      </div>
      {data.managed && <div className="text-amber-400" data-testid="acs-cold-managed-note">Prospects would see DoNotReply@...azurecomm.net, so Azure stays out of cold email until you add a custom domain (guide below). Newsletters and waitlist emails still use Azure, with replies going to the main mailbox.</div>}
      {data.cold && <div className="text-zinc-500">Azure cold emails copy <b className="text-zinc-300">{data.cold_cc}</b>; every reply goes to the main Outlook mailbox, where MaSa tracks the pipeline. Cold email runs weekdays 7am-6pm Sydney (Azure up to {data.per_hour} an hour); newsletters use Azure outside those hours and at weekends.</div>}
      {isAdmin && (
        <div className="flex gap-2">
          <Input data-testid="acs-cold-cc-input" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="sales@specia.ai" className="h-8 bg-black text-xs" />
          <Button size="sm" variant="outline" className="h-8" data-testid="acs-cold-cc-save" onClick={() => save(data.cold)}>Save CC</Button>
        </div>
      )}
    </div>
  );
};

export const AzureEmailPanel = ({ isAdmin }) => {
  const { data, reload } = useFetch("/acs/status");
  const { data: g } = useFetch("/senders");
  const [guide, setGuide] = useState(false);
  const [domainGuide, setDomainGuide] = useState(false);
  if (!data) return null;
  return (
    <Panel title="Azure email (newsletters, waitlist and cold email)" data-testid="acs-panel">
      {!data.configured && data.problem && <p className="text-sm text-red-400 mb-2" data-testid="acs-problem">{data.problem}</p>}
      {!data.configured ? <p className="text-sm text-zinc-400" data-testid="acs-status">Not connected yet. Newsletters, waitlist and cold emails use the current senders until Azure is set up.</p> : (
        <div className="text-sm space-y-1" data-testid="acs-status">
          <div>Sending as <b className="text-white" data-testid="acs-from">{data.from}</b></div>
          <div className="flex gap-3 text-xs"><Tick ok label="Connected" id="acs-connected" /><Tick ok={!data.managed} label={data.managed ? "Free Azure test address" : "Custom domain"} id="acs-domain" /></div>
          <div className="text-xs text-zinc-500" data-testid="acs-usage">{data.sent_1h} of {data.per_hour} sent this hour · {data.sent_24h} in 24h. MaSa paces sends to Azure's limit ({data.per_minute}/min, {data.per_hour}/hour); every email has one-click unsubscribe.</div>
          {data.managed && <div className="text-xs text-amber-400" data-testid="acs-managed-warning">The free address is capped at 10 emails an hour (about 240 a day). Add a custom domain for bigger lists.</div>}
          {data.error && <div className="text-xs text-red-400" data-testid="acs-error">Azure is paused: {data.error}. Everything is going through the Outlook mailboxes until a test passes.</div>}
          {isAdmin && <Button size="sm" variant="outline" className="h-8 mt-1" data-testid="acs-test-btn" onClick={async () => {
            const r = await act(() => api.post("/acs/test"));
            if (r) { r.status === "sent" ? toast.success("Test sent - check your inbox") : toast.error(r.error || r.status); reload(); }
          }}>Send me a test email</Button>}
          <ColdLane data={data} isAdmin={isAdmin} reload={reload} />
          {data.webhook_url && <div className="text-xs text-zinc-500 break-all" data-testid="acs-webhook-url">Bounce link for Azure Events: <span className="text-zinc-300">{data.webhook_url}</span></div>}
        </div>
      )}
      <Button variant="link" size="sm" className="px-0 mt-2 text-[#F97316]" data-testid="acs-guide-toggle" onClick={() => setGuide(!guide)}>{guide ? "Hide" : "Show"} the Azure setup guide</Button>
      {guide && g && <FixGuide id="acs-setup" guide={g.acs_guide} />}
      <Button variant="link" size="sm" className="px-0 ml-4 text-[#F97316]" data-testid="acs-domain-guide-toggle" onClick={() => setDomainGuide(!domainGuide)}>{domainGuide ? "Hide" : "Show"} the custom domain guide (lifts the 10-an-hour limit)</Button>
      {domainGuide && g && <FixGuide id="acs-domain" guide={g.acs_domain_guide} />}
    </Panel>
  );
};
